import { Divider } from "react-native-paper";
import { MAX_ITEM_TYPE_ROWS } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { Skeleton } from "../Skeleton";
import { TipCallout } from "../TipCallout";

const HEADER_MIN_HEIGHT = 72;
const HEADER_ICON_SIZE = 40;
const ROW_ICON_SIZE = 24;
const TITLE_LINE_HEIGHT = 24;
const TITLE_SMALL_LINE_HEIGHT = 20;
const BODY_SMALL_LINE_HEIGHT = 16;
const LABEL_LINE_HEIGHT = 16;
const ROW_TITLE_WIDTHS = ["70%", "55%", "62%"] as const;

const LEVEL_COUNT = 3;
const LEVEL_RING_SIZE_LG = 58;
const LEVEL_RING_THICKNESS_LG = 5;
const LEVEL_CONNECTOR_HEIGHT_LG = 6;
const LEVEL_LABEL_GAP_LG = 6;
const LEVEL_LABEL_HEIGHT = 28;
// Mirrors MAX_LABEL_OVERHANG_PADDING in ConnectedLevels so the loaded rings
// land exactly where the skeleton drew them.
const LEVEL_LABEL_OVERHANG_PADDING = 16;
const SCORE_DONUT_SIZE = 140;
const SCORE_DONUT_THICKNESS = 11;
const LEVEL_CHIP_HEIGHT = 28;
const LEVEL_CHIP_WIDTH = 84;
const CHEVRON_SIZE = 24;
const ROW_TRAILING_GAP = 4;

export type SkillProficiencySkeletonVariant = "levels" | "scores";

type Props = {
	numRows?: number;
	showTipPlaceholder?: boolean;
	proficiencyVariant?: SkillProficiencySkeletonVariant;
};

const TextLine = ({
	lineHeight,
	barHeight,
	width,
}: {
	lineHeight: number;
	barHeight: number;
	width: number | `${number}%`;
}) => (
	<Box height={lineHeight} justifyContent="center">
		<Skeleton width={width} height={barHeight} />
	</Box>
);

const CardHeaderSkeleton = () => {
	const theme = useAppTheme();

	return (
		<>
			<Box
				flexDirection="row"
				alignItems="center"
				minHeight={HEADER_MIN_HEIGHT}
				paddingHorizontal={16}
				gap={16}
			>
				<Skeleton size={HEADER_ICON_SIZE} variant="circle" />
				<Box flex={1} gap={2}>
					<TextLine lineHeight={TITLE_LINE_HEIGHT} barHeight={16} width="60%" />
					<TextLine lineHeight={LABEL_LINE_HEIGHT} barHeight={12} width="40%" />
				</Box>
			</Box>
			<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />
		</>
	);
};

const TipCalloutSkeleton = () => (
	<Box paddingTop={8} paddingHorizontal={16} paddingBottom={16}>
		<TipCallout isLoading />
	</Box>
);

const RingSkeleton = ({
	size,
	thickness,
}: {
	size: number;
	thickness: number;
}) => {
	const theme = useAppTheme();

	return (
		<Skeleton
			size={size}
			variant="circle"
			style={{
				backgroundColor: "transparent",
				borderWidth: thickness,
				borderColor: theme.colors.surfaceVariant,
			}}
		/>
	);
};

const ConnectedLevelsSkeleton = () => (
	<Box
		width="100%"
		flexDirection="row"
		alignItems="flex-start"
		justifyContent="center"
		paddingHorizontal={LEVEL_LABEL_OVERHANG_PADDING}
	>
		{Array.from({ length: LEVEL_COUNT }).map((_, levelIndex) => (
			<Box
				// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rings
				key={levelIndex}
				flexDirection="row"
				alignItems="flex-start"
				flex={levelIndex > 0 ? 1 : undefined}
			>
				{levelIndex > 0 ? (
					<Box
						flex={1}
						marginTop={LEVEL_RING_SIZE_LG / 2 - LEVEL_CONNECTOR_HEIGHT_LG / 2}
					>
						<Skeleton height={LEVEL_CONNECTOR_HEIGHT_LG} />
					</Box>
				) : null}
				<Box
					width={LEVEL_RING_SIZE_LG}
					alignItems="center"
					gap={LEVEL_LABEL_GAP_LG}
				>
					<RingSkeleton
						size={LEVEL_RING_SIZE_LG}
						thickness={LEVEL_RING_THICKNESS_LG}
					/>
					<Box
						height={LEVEL_LABEL_HEIGHT}
						justifyContent="center"
						alignItems="center"
					>
						<Skeleton width={56} height={14} />
					</Box>
				</Box>
			</Box>
		))}
	</Box>
);

const ScoreDonutSkeleton = () => (
	<Box gap={16} alignItems="center">
		<RingSkeleton size={SCORE_DONUT_SIZE} thickness={SCORE_DONUT_THICKNESS} />
		<Box gap={4} alignItems="center">
			<TextLine
				lineHeight={TITLE_SMALL_LINE_HEIGHT}
				barHeight={14}
				width={72}
			/>
			<TextLine lineHeight={BODY_SMALL_LINE_HEIGHT} barHeight={12} width={56} />
		</Box>
	</Box>
);

const ProficiencyCardSkeleton = ({
	showTipPlaceholder,
	proficiencyVariant = "levels",
}: Pick<Props, "showTipPlaceholder" | "proficiencyVariant">) => {
	const { isMobile } = useDevice();

	return (
		<FlatCard
			style={{
				height: isMobile ? undefined : "100%",
				overflow: "hidden",
			}}
			contentStyle={{ flex: isMobile ? undefined : 1 }}
		>
			<CardHeaderSkeleton />
			<Box gap={8} flex={isMobile ? undefined : 1}>
				<Box
					flex={isMobile ? undefined : 1}
					alignItems="center"
					justifyContent="center"
					paddingVertical={isMobile ? 24 : 42}
					paddingHorizontal={16}
				>
					{proficiencyVariant === "scores" ? (
						<ScoreDonutSkeleton />
					) : (
						<ConnectedLevelsSkeleton />
					)}
				</Box>
				{showTipPlaceholder ? <TipCalloutSkeleton /> : null}
			</Box>
		</FlatCard>
	);
};

const LevelChipSkeleton = () => {
	const theme = useAppTheme();

	return (
		<Skeleton
			height={LEVEL_CHIP_HEIGHT}
			width={LEVEL_CHIP_WIDTH}
			borderRadius={theme.roundness}
		/>
	);
};

const ItemTypesCardSkeleton = ({
	numRows = MAX_ITEM_TYPE_ROWS,
	showTipPlaceholder,
}: Pick<Props, "numRows" | "showTipPlaceholder">) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	return (
		<FlatCard
			style={{
				height: isMobile ? undefined : "100%",
				overflow: "hidden",
			}}
			contentStyle={{ flex: isMobile ? undefined : 1 }}
		>
			<CardHeaderSkeleton />
			<Box gap={8} flex={isMobile ? undefined : 1}>
				<Box flex={isMobile ? undefined : 1}>
					{Array.from({ length: numRows }).map((_, rowIndex) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
						<Box key={rowIndex}>
							{rowIndex > 0 ? (
								<Divider
									style={{
										marginHorizontal: 16,
										backgroundColor: theme.colors.surfaceVariant,
									}}
								/>
							) : null}
							<Box
								flexDirection="row"
								alignItems="center"
								gap={8}
								paddingVertical={8}
								paddingHorizontal={16}
							>
								<Box
									width={32}
									height={32}
									alignItems="center"
									justifyContent="center"
								>
									<Skeleton size={ROW_ICON_SIZE} variant="circle" />
								</Box>
								<Box flex={1} justifyContent="center" gap={4}>
									<TextLine
										lineHeight={TITLE_SMALL_LINE_HEIGHT}
										barHeight={14}
										width={ROW_TITLE_WIDTHS[rowIndex % ROW_TITLE_WIDTHS.length]}
									/>
									<TextLine
										lineHeight={BODY_SMALL_LINE_HEIGHT}
										barHeight={12}
										width="35%"
									/>
								</Box>
								<Box
									flexDirection="row"
									alignItems="center"
									gap={ROW_TRAILING_GAP}
								>
									<LevelChipSkeleton />
									<Skeleton size={CHEVRON_SIZE} variant="circle" />
								</Box>
							</Box>
						</Box>
					))}
				</Box>
				{showTipPlaceholder ? <TipCalloutSkeleton /> : null}
			</Box>
		</FlatCard>
	);
};

export const SkillSectionSkeleton = ({
	numRows,
	showTipPlaceholder = true,
	proficiencyVariant,
}: Props) => {
	const { isMobile } = useDevice();

	return (
		<Box gap={isMobile ? 16 : 8}>
			<Box paddingHorizontal={isMobile ? 16 : 0}>
				<TextLine lineHeight={TITLE_LINE_HEIGHT} barHeight={16} width={120} />
			</Box>
			{isMobile ? (
				<Box gap={16}>
					<ProficiencyCardSkeleton
						showTipPlaceholder={showTipPlaceholder}
						proficiencyVariant={proficiencyVariant}
					/>
					<ItemTypesCardSkeleton
						numRows={numRows}
						showTipPlaceholder={showTipPlaceholder}
					/>
				</Box>
			) : (
				<Box flexDirection="row" alignItems="stretch" gap={16}>
					<Box flex={1}>
						<ProficiencyCardSkeleton
							showTipPlaceholder={showTipPlaceholder}
							proficiencyVariant={proficiencyVariant}
						/>
					</Box>
					<Box flex={1}>
						<ItemTypesCardSkeleton
							numRows={numRows}
							showTipPlaceholder={showTipPlaceholder}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};
